<div class="auth-container">
  <!-- Background with gradient -->
  <div class="auth-background"></div>
  
  <!-- Main auth card -->
  <div class="auth-card" [class.register-mode]="!isLoginMode">
    
    <!-- Header Section - Compact like Stripe -->
    <div class="auth-header">
      <h1 class="auth-title">{{ isLoginMode ? ('AUTH.TITLES.LOGIN' | productionTranslate) : ('AUTH.TITLES.REGISTER' | productionTranslate) }}</h1>
    </div>

    <!-- Auth Form (Email/Password) -->
    <form class="auth-form" [formGroup]="authForm" (ngSubmit)="onSubmit()">
      
      <!-- Full Name Field (Register only) -->
      <div class="form-group" *ngIf="!isLoginMode">
        <label for="fullname" class="form-label">
          {{ 'AUTH.FIELDS.FULL_NAME' | productionTranslate }}
          <span class="required" [attr.aria-label]="'COMMON.MISC.REQUIRED_FIELD' | productionTranslate">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="text"
            id="fullname"
            formControlName="fullname"
            class="form-input"
            [class.error]="authForm.get('fullname')?.invalid && authForm.get('fullname')?.touched"
            [placeholder]="'AUTH.PLACEHOLDERS.FULL_NAME' | productionTranslate"
            autocomplete="name"
            required>
          <div class="input-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
        </div>
        <div class="field-error" *ngIf="authForm.get('fullname')?.invalid && authForm.get('fullname')?.touched">
          {{ 'AUTH.MESSAGES.VALIDATION.FULLNAME_REQUIRED' | productionTranslate }}
        </div>
      </div>

      <!-- Email Field -->
      <div class="form-group">
        <label for="email" class="form-label">
          {{ 'AUTH.FIELDS.EMAIL' | productionTranslate }}
          <span class="required" [attr.aria-label]="'COMMON.MISC.REQUIRED_FIELD' | productionTranslate">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="email"
            id="email"
            formControlName="email"
            class="form-input"
            [class.error]="authForm.get('email')?.invalid && authForm.get('email')?.touched"
            [class.success]="authForm.get('email')?.valid && authForm.get('email')?.touched"
            [placeholder]="'AUTH.PLACEHOLDERS.EMAIL' | productionTranslate"
            autocomplete="email"
            required>
          <div class="input-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
          </div>
          <div class="validation-icon" *ngIf="authForm.get('email')?.touched">
            <svg *ngIf="authForm.get('email')?.valid" class="success-icon" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <svg *ngIf="authForm.get('email')?.invalid" class="error-icon" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </div>
        </div>
        <div class="field-error" *ngIf="authForm.get('email')?.invalid && authForm.get('email')?.touched">
          <span *ngIf="authForm.get('email')?.errors?.['required']">{{ 'AUTH.MESSAGES.VALIDATION.EMAIL_REQUIRED' | productionTranslate }}</span>
          <span *ngIf="authForm.get('email')?.errors?.['email']">{{ 'AUTH.MESSAGES.VALIDATION.VALID_EMAIL_REQUIRED' | productionTranslate }}</span>
        </div>
      </div>

      <!-- Password Field -->
      <div class="form-group">
        <label for="password" class="form-label">
          {{ 'AUTH.FIELDS.PASSWORD' | productionTranslate }}
          <span class="required" [attr.aria-label]="'COMMON.MISC.REQUIRED_FIELD' | productionTranslate">*</span>
        </label>
        <div class="input-wrapper">
          <input
            [type]="showPassword ? 'text' : 'password'"
            id="password"
            formControlName="password"
            class="form-input"
            [class.error]="authForm.get('password')?.invalid && authForm.get('password')?.touched"
            [class.success]="authForm.get('password')?.valid && authForm.get('password')?.touched"
            [placeholder]="'AUTH.PLACEHOLDERS.PASSWORD' | productionTranslate"
            autocomplete="current-password"
            required>
          <button
            type="button"
            class="password-toggle"
            (click)="togglePasswordVisibility()"
            [attr.aria-label]="showPassword ? ('AUTH.ACTIONS.HIDE_PASSWORD' | productionTranslate) : ('AUTH.ACTIONS.SHOW_PASSWORD' | productionTranslate)">
            <svg *ngIf="!showPassword" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
            </svg>
            <svg *ngIf="showPassword" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
            </svg>
          </button>
        </div>
        <div class="password-strength" *ngIf="!isLoginMode && authForm.get('password')?.value">
          <div class="strength-meter">
            <div class="strength-bar" [class]="getPasswordStrength().class"></div>
          </div>
          <span class="strength-text">{{ getPasswordStrength().text | productionTranslate }}</span>
        </div>
        <div class="field-error" *ngIf="authForm.get('password')?.invalid && authForm.get('password')?.touched">
          <span *ngIf="authForm.get('password')?.errors?.['required']">{{ 'AUTH.MESSAGES.VALIDATION.PASSWORD_REQUIRED' | productionTranslate }}</span>
          <span *ngIf="authForm.get('password')?.errors?.['minlength']">{{ 'AUTH.MESSAGES.VALIDATION.PASSWORD_MIN_LENGTH' | productionTranslate }}</span>
        </div>
      </div>

      <!-- Confirm Password Field (Register only) -->
      <div class="form-group" *ngIf="!isLoginMode">
        <label for="password2" class="form-label">
          {{ 'AUTH.FIELDS.CONFIRM_PASSWORD' | productionTranslate }}
          <span class="required" [attr.aria-label]="'COMMON.MISC.REQUIRED_FIELD' | productionTranslate">*</span>
        </label>
        <div class="input-wrapper">
          <input
            type="password"
            id="password2"
            formControlName="password2"
            class="form-input"
            [class.error]="authForm.get('password2')?.invalid && authForm.get('password2')?.touched"
            [class.success]="authForm.get('password2')?.valid && authForm.get('password2')?.touched"
            [placeholder]="'AUTH.PLACEHOLDERS.CONFIRM_PASSWORD' | productionTranslate"
            autocomplete="new-password"
            required>
          <div class="validation-icon" *ngIf="authForm.get('password2')?.touched">
            <svg *ngIf="authForm.get('password2')?.valid && passwordsMatch()" class="success-icon" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <svg *ngIf="authForm.get('password2')?.invalid || !passwordsMatch()" class="error-icon" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </div>
        </div>
        <div class="field-error" *ngIf="(authForm.get('password2')?.invalid && authForm.get('password2')?.touched) || (!passwordsMatch() && authForm.get('password2')?.touched)">
          <span *ngIf="authForm.get('password2')?.errors?.['required']">{{ 'AUTH.MESSAGES.VALIDATION.CONFIRM_PASSWORD_REQUIRED' | productionTranslate }}</span>
          <span *ngIf="!passwordsMatch() && authForm.get('password2')?.value">{{ 'AUTH.MESSAGES.VALIDATION.PASSWORDS_DONT_MATCH' | productionTranslate }}</span>
        </div>
      </div>

      <!-- Remember Me / Forgot Password -->
      <div class="form-options" *ngIf="isLoginMode">
        <label class="checkbox-wrapper">
          <input type="checkbox" formControlName="rememberMe">
          <span class="checkmark"></span>
          <span class="checkbox-label">{{ 'AUTH.FIELDS.REMEMBER_ME' | productionTranslate }}</span>
        </label>
        <a routerLink="/reset-password" class="forgot-password-link">
          {{ 'AUTH.LINKS.FORGOT_PASSWORD' | productionTranslate }}
        </a>
      </div>

      <!-- Account Recovery Link (for phone-only users who lost their phone) -->
      <div class="recovery-link-container" *ngIf="isLoginMode">
        <a routerLink="/auth/account-recovery" class="recovery-link">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
          </svg>
          {{ 'AUTH.LINKS.LOST_PHONE' | productionTranslate }}
        </a>
      </div>

      <!-- Error Messages -->
      <div class="auth-error" *ngIf="errorMessage">
        <svg class="error-icon" viewBox="0 0 24 24">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
        </svg>
        <span>{{ errorMessage | productionTranslate }}</span>
      </div>

      <!-- Submit Button -->
      <button 
        type="submit" 
        class="auth-submit-btn"
        [disabled]="!authForm.valid || isLoading"
        [class.loading]="isLoading">
        <span class="btn-text" *ngIf="!isLoading">
          {{ isLoginMode ? ('AUTH.ACTIONS.LOGIN' | productionTranslate) : ('AUTH.ACTIONS.CREATE_ACCOUNT' | productionTranslate) }}
        </span>
        <div class="btn-loader" *ngIf="isLoading">
          <div class="spinner"></div>
          <span>{{ 'AUTH.MESSAGES.INFO.CONNECTING' | productionTranslate }}</span>
        </div>
      </button>

    </form>

    <!-- Divider -->
    <div class="auth-divider">
      <span>{{ 'COMMON.MISC.OR' | productionTranslate }}</span>
    </div>

    <!-- Social Login Buttons -->
    <div class="social-auth-section">
      <button
        type="button"
        class="social-btn google-btn"
        (click)="onGoogleLogin()"
        [disabled]="isLoading"
        [attr.aria-label]="'AUTH.SOCIAL.GOOGLE_LOGIN' | productionTranslate">
        <svg class="social-icon" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span>{{ 'AUTH.SOCIAL.CONTINUE_WITH_GOOGLE' | productionTranslate }}</span>
      </button>

      <!-- Phone Login Button -->
      <button
        type="button"
        class="social-btn phone-btn"
        (click)="switchToPhoneAuth()"
        [disabled]="isLoading"
        [attr.aria-label]="'AUTH.SOCIAL.PHONE_LOGIN' | productionTranslate">
        <svg class="social-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
        </svg>
        <span>{{ 'AUTH.SOCIAL.CONTINUE_WITH_PHONE' | productionTranslate }}</span>
      </button>
    </div>

    <!-- Switch Mode -->
    <div class="auth-switch">
      <p>
        <span *ngIf="isLoginMode">{{ 'AUTH.MESSAGES.INFO.NO_ACCOUNT' | productionTranslate }}</span>
        <span *ngIf="!isLoginMode">{{ 'AUTH.MESSAGES.INFO.ALREADY_HAVE_ACCOUNT' | productionTranslate }}</span>
        <button
          type="button"
          class="switch-btn"
          (click)="switchMode()"
          [disabled]="isLoading">
          {{ isLoginMode ? ('AUTH.ACTIONS.REGISTER' | productionTranslate) : ('AUTH.ACTIONS.LOGIN' | productionTranslate) }}
        </button>
      </p>
    </div>

    <!-- Terms (Register only) -->
    <div class="auth-terms" *ngIf="!isLoginMode">
      <p>
        {{ 'AUTH.MESSAGES.INFO.TERMS_INTRO' | productionTranslate }}
        <a routerLink="/conditions" target="_blank">{{ 'AUTH.LINKS.TERMS_OF_SERVICE' | productionTranslate }}</a>
        {{ 'AUTH.MESSAGES.INFO.AND_OUR' | productionTranslate }}
        <a routerLink="/privacy" target="_blank">{{ 'AUTH.LINKS.PRIVACY_POLICY' | productionTranslate }}</a>.
      </p>
    </div>

    <!-- Employee portal link -->
    <div class="employee-portal-section">
      <a routerLink="/backoffice-login" class="employee-portal-link">
        <i class="fas fa-building"></i> {{ 'COMMON.NAVIGATION.EMPLOYEE_AREA' | productionTranslate }}
      </a>
    </div>

  </div>

  <!-- Loading overlay -->
  <div class="loading-overlay" *ngIf="isLoading">
    <div class="loading-spinner"></div>
  </div>

</div>